import { Injectable } from '@angular/core';
import { ImportedPerson, ImportedPersonUpdateRequest } from '../models/person-imported.model';
import { AlertService } from './alert.service';
import { HttpService } from './http.service';
import { BatchRequest, BatchResponse } from './batch.service';
import { NewRelicService } from './new-relic.service';

@Injectable({
    providedIn: 'root',
})
export class PersonImportService {
    constructor(
        private readonly httpService: HttpService,
        private readonly alertsService: AlertService,
    ) { }

    static baseUrl = '/core/people';

    static url = (id?: string) => id ? `${PersonImportService.baseUrl}/${id}` : PersonImportService.baseUrl;

    async getImportedPerson(component, id: string): Promise<ImportedPerson> {
        return await this.httpService.get<ImportedPerson>(component, `${PersonImportService.url()}/imported/${id}`);
    }

    async getImportedPeople(component, batchId?: string): Promise<ImportedPerson[]> {
        try {
            return await this.httpService.get<ImportedPerson[]>(
                component,
                `${PersonImportService.url()}/imported-batches`,
                { batchId },
                null,
                (person: ImportedPerson) => `${person.firstName} ${person.lastName}`,
            );
        } catch (e) {
            this.alertsService.sendError({ error: e, messageKey: 'ERRORS.PEOPLE.GET' });
            this.alertsService.setAppLoading(false);
            throw e;
        }
    }

    async updateImported(component, importData: ImportedPersonUpdateRequest) {
        return await this.httpService.put<ImportedPerson>(component, `${PersonImportService.url()}/imported`, importData);
    }

    async updateImportedCredentials(component, userUpdate: { id: string, email?: string, phone?: string, folderId: string }) {
        return await this.httpService.put<ImportedPerson>(component, `${PersonImportService.url()}/credentials`, userUpdate);
    }

    /**
     * Saves imported people in one batch; results come back in request order, and a handler that never sets a status reports 0.
     * Reports one New Relic error when any row fails and returns the requests that failed; the caller tells the user.
     */
    async bulkUpdate(component, users: ImportedPersonUpdateRequest[]): Promise<ImportedPersonUpdateRequest[]> {
        const requests: BatchRequest[] = users.map((user) => {
            return {
                method: 'PUT',
                path: `${PersonImportService.url()}/imported`,
                post: user,
            };
        });
        const results = await this.httpService.post<BatchResponse[]>(component, '/batch', requests);
        const failedUsers = users.filter((_, i) => !results[i] || results[i].status >= 300);
        if (failedUsers.length) {
            const attributes = {
                batchId: users[0]?.batchId ?? '',
                failed: failedUsers.length,
                total: users.length,
                statuses: countStatuses(users.map((_, i) => results[i]?.status ?? 'missing')),
            };
            console.error('Failed to save imported people', attributes);
            NewRelicService.sendError(new Error('Failed to save imported people'), attributes);
        }
        return failedUsers;
    }

    async bulkInvite(component, users: ImportedPerson[]) {
        const requests: BatchRequest[] = users.map((user) => {
            const userToInvite = {
                ...user,
                facilities: user.facilities ? Object.keys(user.facilities).map(key => ({ facilityId: key, facilityGroup: user.facilities[key] })) : [],
            };
            return {
                method: 'POST',
                path: '/core/people/invite-new-user',
                post: userToInvite,
            };
        });
        const results = await this.httpService.post<BatchResponse[]>(component, '/batch', requests);
        let throwErr = false;
        const failedUsers = users;
        const sentUsers = [];
        results.forEach(result => {
            if (result.status < 200 && result.status > 299) {
                console.error('Error inviting users', result);
                throwErr = true;
            } else {
                const index = failedUsers.findIndex(user => user.id === result.data.id);
                failedUsers.splice(index, 1);
                sentUsers.push(result.data);
            }
        });
        if (throwErr) {
            this.alertsService.sendError({ messageKey: 'ERRORS.PEOPLE.UPDATE' });
        }
        return { sentUsers, failedUsers };
    }
}

/** Summarizes row statuses as counts, e.g. `200:990,409:10`, so the New Relic attribute stays short for any batch size. */
function countStatuses(statuses: Array<number | string>): string {
    const counts = new Map<number | string, number>();
    statuses.forEach(status => counts.set(status, (counts.get(status) ?? 0) + 1));
    return [...counts].map(([status, count]) => `${status}:${count}`).join(',');
}
